import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import debounce from 'lodash/debounce';

import ActionsButton from '@common/components/ActionsButton';
import Tree from '@common/components/Tree';
import { Button } from '@common/components/Button';
import { Flex } from '@common/components/Flex';
import { Header } from '@common/components/Header';
import { Label } from '@common/components/Label';
import { Table, HIGHLIGHT_BACKGROUND_COLOR } from '@common/components/Table';
import { MessageBox } from '@common/components/MessageBox';
import { deleteFormat, editSpreadsheet, getFormats, openExistedSpreadsheet, openNewSpreadsheet, getFormatsColumnsWidth, setFormatColumnsWidth, canDeleteSpreadsheet } from '../../actions/formats';
import { getFolders } from '../../actions/folders';
import { getAppConfig } from '@common/utils/config';
import { SPREADSHEET_TYPE } from '../../const/common';
import { compareDates } from '@common/utils/date';

import './SpreadSheetsPedigree.styles.scss';

export class SpreadSheetsPedigree extends React.Component {
    appConfig = getAppConfig();

    state = {
        messageProps: {
            header: I18n.t('Delete Format'),
            text: '',
            show: false,
            confirmText: 'Ok',
            cancelText: I18n.t('Cancel'),
            size: 'small',
            hideMessageBox: () => this.setMessageProps({ show: false }),
            onCustomOk: () => {},
        },
        folders: [],
        formats: [],
        formatsColumnsWidth: [],
        collapsedFolders: [],
        selectedFormatId: null,
        selectedFolderId: null,
        canDeleteSpreadsheet: true,
    }

    componentDidMount() {
        this.initialize();
    }

    initialize = async () => {
        canDeleteSpreadsheet().then((canDelete) => {
            this.setState((state) => ({ ...state, canDeleteSpreadsheet: canDelete }));
        });

        try {
            await getFormatsColumnsWidth(SPREADSHEET_TYPE.pedigree).then((formatsColumnsWidth) => {
                this.setState((x) => ({
                    ...x,
                    formatsColumnsWidth,
                }));
            });
        } catch (err) {
            console.error(err);
        }

        const folders = await getFolders(SPREADSHEET_TYPE.pedigree);

        this.setState((x) => ({
            ...x,
            folders,
        }));

        const defaultFolderId = this.getDefaultFolderId(folders);

        if (defaultFolderId !== undefined) {
            this.setSelectedFolder(defaultFolderId, () => this.getFormats({ folderId: this.state.selectedFolderId }));
        }
    }

    getDefaultFolderId(folders = this.state.folders) {
        const firstFolder = folders[0];

        return firstFolder?.id;
    }

    getTrProps = (state, rowInfo, column) => {
        const { original: { id } } = rowInfo;
        const { selectedFormatId } = this.state;
        let background = undefined;

        if (selectedFormatId == id) {
            background = HIGHLIGHT_BACKGROUND_COLOR;
        }

        return {
            onClick: () => this.setSelectedFormat(id),
            style: {
                background
            }
        };
    }

    setMessageProps = (props) => {
        this.setState((state) => ({
            ...state,
            messageProps: {
                ...state.messageProps,
                ...props,
            },
        }));
    }

    closeMessage = () => {
        this.setMessageProps({ show: false });
    }

    searchFormats = ({ searchKeyword }) => {
        const hasSearchKeyword = Boolean(searchKeyword);
        const selectedFolder = hasSearchKeyword 
            ? null
            : this.getDefaultFolderId();

        this.setSelectedFolder(
            selectedFolder,
            () => this.setCollapsedFolders([])
        );

        this.getFormats({ 
            folderId: searchKeyword ? undefined : selectedFolder,
            searchKeyword,
        });
    }

    getFormats = ({ searchKeyword, folderId }) => {
        return getFormats(SPREADSHEET_TYPE.pedigree, { searchKeyword, folderId }).then((formats) => {
            this.setState((x) => ({
                ...x,
                formats,
            }));
        });
    }

    setSelectedFolder = (id, callback) => {
        this.setState(
            (state) => ({
                ...state,
                selectedFolderId: id
            }), 
            callback
        );
    }

    setCollapsedFolders = (folders) => {
        this.setState(
            (state) => ({
                ...state,
                collapsedFolders: folders
            }),
        );
    }

    setSelectedFormat = (id, callback) => {
        this.setState(
            (state) => ({
                ...state,
                selectedFormatId: id
            }), 
            callback
        );
    }

    handleRunSpreadsheet = (id, data) => {
        openExistedSpreadsheet(SPREADSHEET_TYPE.pedigree, id, data.name);
    }

    handleEditSpreadsheet = (id, data) => {
        editSpreadsheet(SPREADSHEET_TYPE.pedigree, id, data.name);
    }

    deleteFormat = (id) => {
        return deleteFormat(SPREADSHEET_TYPE.pedigree, id);
    }

    handleDeleteSpreadsheet = (id) => {
        this.setMessageProps({
            show: true,
            text: I18n.t('Are you sure you want to delete the selected format?'),
            onCustomOk: () => this.deleteFormat(id)
                .then(() => this.getFormats({ folderId: this.state.selectedFolderId || this.getDefaultFolderId() }))
                .then(this.closeMessage)
        });
    }

    handleAddNewSpreadsheet = () => {
        openNewSpreadsheet(SPREADSHEET_TYPE.pedigree);
    }

    handleFolderChange = (id) => {
        this.setSelectedFolder(id, () => this.getFormats({ folderId: this.state.selectedFolderId }));
    }

    getColumnWidth = (columnName, defaultWidth) => {
        const { formatsColumnsWidth } = this.state;

        const config = formatsColumnsWidth.find((column) => {
            return column.columnName.toLowerCase() === columnName.toLowerCase();
        });

        if (config) {
            return config.columnWith || defaultWidth;
        }

        return defaultWidth;
    }

    handleColumnResize = (columns) => {
        columns.forEach(({ id, value }) => {
            setFormatColumnsWidth(SPREADSHEET_TYPE.pedigree, { columnName: id, columnWidth: Math.round(value) });
        });
    }

    get columns() {
        const actions = [
            { label: I18n.t('Run Spreadsheet'), onClick: this.handleRunSpreadsheet  },
            { label: I18n.t('Edit Spreadsheet'), onClick: this.handleEditSpreadsheet },
            { label: I18n.t('Delete Spreadsheet'), onClick: this.handleDeleteSpreadsheet, condition: this.state.canDeleteSpreadsheet },
        ].filter(({ condition = true }) => condition);

        return [
            { 
                resizable: false,
                sortable: false,
                computeWidth: false,
                Header: 'Actions', 
                Cell: ({ original }) => (
                    <ActionsButton 
                        id={original.id} 
                        data={original} 
                        menuItems={actions} 
                        setCurrent={this.setSelectedFormat} 
                    />
                ),
                minWidth: 65,
                className: 'table-actions-cell'
            },
            { 
                Header: 'Format Name', 
                accessor: 'name', 
                minWidth: 200, 
                Cell: ({ original }) => (
                    <Button 
                        variant="text"
                        text={original.name}
                        onClick={() => this.handleRunSpreadsheet(original.id, original)}
                    />
                ),
            },
            { Header: 'Description', accessor: 'description', minWidth: 200, },
            { Header: 'Folder', accessor: 'foldername', minWidth: 120 },
            { Header: 'Modified By', accessor: 'modifiedby', minWidth: 120 },
            { Header: 'Date Modified', accessor: 'lastmodified', minWidth: 120, sortMethod: compareDates },
            { Header: 'Created By', accessor: 'createdBy', minWidth: 120 },
            { Header: 'Date Created', accessor: 'createdDate', minWidth: 120, sortMethod: compareDates },
        ].map(({ sortable = true, computeWidth = true, Header, minWidth, ...otherProps }) => {
            return {
                sortable,
                minWidth,
                Header: I18n.t(Header),
                width: computeWidth ? this.getColumnWidth(Header, minWidth) : minWidth,
                ...otherProps,
            };
        });
    }

    render() {
        const { formats, folders } = this.state;
        const pedigreeSpreadsheetConfig = this.appConfig.pedigreeSpreadsheet;
        const { collapsedFolders } = this.state;

        return (
            <div className="SpreadSheetsPedigree">
                <MessageBox
                    {...this.state.messageProps}
                />
                <Header 
                    moduleName="Pedigree Spreadsheets"
                    className="SpreadSheetsPedigree__header"
                    withLiveSearch={pedigreeSpreadsheetConfig.liveSearchEnabled}
                    liveSearchDelay={pedigreeSpreadsheetConfig.liveSearchDelay}
                    onSearch={this.searchFormats}
                >
                    <button className="btn btn-primary" onClick={this.handleAddNewSpreadsheet}>
                        <i className="fa fa-plus"></i>
                        {I18n.t('New Spreadsheet')}
                    </button>
                </Header>
                <Flex width="100%" wrap="nowrap" className="SpreadSheetsPedigree__content">
                    <div className="SpreadSheetsPedigree__content__folder">
                        <Label className="SpreadSheetsPedigree__content__folder__label" text={I18n.t("Folder")} />
                        <Tree
                            className="SpreadSheetsPedigree__content__folder__inner"
                            items={folders}
                            value={this.state.selectedFolderId} 
                            collapsedItems={collapsedFolders}
                            onChange={this.handleFolderChange} 
                            onCollapsedItemsChange={this.setCollapsedFolders}
                        />
                    </div>
                    <div className="SpreadSheetsPedigree__content__table">
                        <Table
                            disableAutoInit
                            serverSorting={false}
                            noDataMessage={I18n.t('There no pedigree spreadsheets to display')}
                            config={{
                                data: formats,
                                columns: this.columns,
                                getTrProps: this.getTrProps,
                                onResizedChange: debounce(this.handleColumnResize, 500)
                            }}
                        />
                    </div>
                </Flex>
            </div>
        );
    }
}
